// src/pages/NewOrderPage.jsx
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { createOrder, getProducts, getCustomers, getEmployees, upsertCustomerFromOrder } from '../lib/db';
import { schedule, workflowOf, fmtDay, relativePhrase, fmtTime, todayStr } from '../lib/priority';
import { titleCaseName, sentenceCase } from '../lib/text';

const ORDER_TYPES = [
  { id: 'carry_out',      label: 'Carry Out',       icon: '🛍' },
  { id: 'local_delivery', label: 'Local Delivery',  icon: '🚚' },
  { id: 'pick_up',        label: 'Pick Up',         icon: '📦' },
  { id: 'wire_out',       label: 'Wire Out',        icon: '📤' },
  { id: 'wire_in',        label: 'Wire In',         icon: '📥' },
];

const OCCASIONS = ['Other','Birthday','Anniversary','Sympathy','Congratulations','Get Well','Romance','Thank You','New Baby','Funeral'];
const METHODS   = ['Credit Card','Cash','Debit','Check','Invoice'];

const fmt = (n) => '$' + (n || 0).toFixed(2);

function emptyItem() { return { productId:'', description:'', qty:1, price:0, discount:0 }; }

/**
 * Grow a description box to fit its text instead of scrolling it.
 *
 * The border width has to be added back: the box is border-box, but
 * scrollHeight measures the padding box, so height = scrollHeight leaves the
 * last line a pixel or two short and a scrollbar appears.
 */
function autoGrow(el) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = el.scrollHeight + (el.offsetHeight - el.clientHeight) + 'px';
}

export function NewOrderPage({ setPage }) {
  const { shopId, shop, profile } = useAuth();
  const [type,     setType]     = useState('local_delivery');
  const [products, setProducts] = useState([]);
  const [customers,setCustomers]= useState([]);
  const [saving,   setSaving]   = useState(false);
  const [success,  setSuccess]  = useState('');
  const [error,    setError]    = useState('');
  const [customDelivery, setCustomDelivery] = useState(null); // null = use shop default
  const [customTaxRate,  setCustomTaxRate]  = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  // Pick-ups and carry-outs are usually collected by whoever ordered them.
  const [sameAsCustomer, setSameAsCustomer] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [soldBy,    setSoldBy]    = useState('');

  // Form state
  const [custSearch, setCustSearch] = useState('');
  const [custResults,setCustResults]= useState([]);
  const [selectedCust,setSelectedCust]= useState(null);

  const [billing, setBilling] = useState({
    firstName:'', lastName:'', address:'', city: shop?.city || 'Yellowknife',
    stPr: shop?.province || 'NT', zipPostal:'', country:'Canada', phone:'', email:'',
    cardNumber:'', expDate:'', ccv:'', auth:'', poNumber:'',
    keepOnFile: true, internetTx: false, dontProcess: false,
    // Explicit paid flag. Matters most on Pick Up, where the customer collects
    // later and the counter needs to know whether to take money on handover.
    paid: false,
    method: 'Credit Card',
  });
  const [recipient, setRecipient] = useState({
    firstName:'', lastName:'', address:'', city:'Yellowknife', stPr:'NT',
    zipPostal:'', country:'Canada', phone:'', cardMessage:'', occasion:'Other', facility:''
  });
  const [delivery, setDelivery] = useState({
    date: new Date().toISOString().split('T')[0],
    time:'None', status:'Ready', zone:'', driver:'',
    designStatus:'Pending', designer:'None', instructions:'',
  });
  const [wireOut, setWireOut] = useState({
    sendVia:'Teleflora-Dove', fillingFlorist:'', autoForward:'Y', confirmation:false,
  });
  const [wireIn, setWireIn] = useState({
    receivedVia:'Teleflora-Phone', deliveryType:'Local Delivery',
    floristId:'', floristInfo:'', contactName:'', refOrderNumber:'',
    dontReportTeleflora: false, employee:'None',
  });

  // Out-of-town orders WE make and put on a plane. Distinct from wire_out,
  // where another florist does the work.
  const [outOfTown, setOutOfTown] = useState(false);
  const [flight,    setFlight]    = useState({ airline:'', number:'', date:'', departTime:'' });

  const [items,  setItems]  = useState([emptyItem()]);
  const [items2, setItems2] = useState([{ productId:'OT', description:'Similar', qty:1, price:0, discount:0 }]); // second choice

  // Totals — delivery and tax can be overridden per order
  const subtotal    = items.reduce((s,i) => s + (i.qty * i.price * (1 - i.discount/100)), 0);
  const defaultDelivery = ['local_delivery','wire_in'].includes(type) ? (shop?.deliveryFee || 12) : 0;
  const deliveryFee = customDelivery !== null ? customDelivery : defaultDelivery;
  const serviceFee  = type === 'wire_out' ? (shop?.serviceFee || 14.99) : 0;
  const taxRate     = customTaxRate !== null ? customTaxRate : (shop?.taxRate || 0.05);
  const tax         = (subtotal + serviceFee) * taxRate;
  const total       = subtotal + deliveryFee + serviceFee + tax;

  // Live scheduling preview — tells the person taking the order, right now,
  // which day this has to be made on. Same engine the workboard uses.
  const wf = useMemo(() => workflowOf(shop), [shop]);
  const preview = useMemo(() => schedule({
    total,
    recipient,
    fulfillment: {
      neededDate: delivery.date,
      neededTime: delivery.time,
      outOfTown,
      flight: outOfTown ? flight : null,
    },
  }, wf), [total, recipient, delivery.date, delivery.time, outOfTown, flight, wf]);

  useEffect(() => {
    if (!sameAsCustomer) return;
    setRecipient(r => ({
      ...r,
      firstName: billing.firstName, lastName: billing.lastName,
      address:   billing.address,   city:     billing.city,
      stPr:      billing.stPr,      zipPostal: billing.zipPostal,
      country:   billing.country,   phone:    billing.phone,
    }));
  }, [sameAsCustomer, billing.firstName, billing.lastName, billing.address,
      billing.city, billing.stPr, billing.zipPostal, billing.country, billing.phone]);

  useEffect(() => {
    if (!shopId) return;
    getProducts(shopId).then(setProducts);
    getCustomers(shopId).then(setCustomers);
    getEmployees(shopId).then(setEmployees).catch(e => console.warn('[employees]', e));
  }, [shopId]);

  // Default to whoever is signed in, once their profile has loaded.
  useEffect(() => {
    if (!soldBy && profile?.displayName) setSoldBy(profile.displayName);
  }, [profile?.displayName, soldBy]);

  // Customer search — matches name/email by substring, and phone number
  // digit-insensitively, so "8675550134" finds "(867) 555-0134".
  useEffect(() => {
    if (!custSearch.trim()) { setCustResults([]); return; }
    const t = custSearch.toLowerCase();
    const digits = t.replace(/\D/g, '');
    setCustResults(customers.filter(c => {
      const haystack = `${c.firstName} ${c.lastName} ${c.phone} ${c.email}`.toLowerCase();
      if (haystack.includes(t)) return true;
      const custDigits = (c.phone || '').replace(/\D/g, '');
      return digits.length >= 3 && custDigits.includes(digits);
    }).slice(0, 6));
  }, [custSearch, customers]);

  function selectCustomer(c) {
    setSelectedCust(c);
    setCustSearch(`${c.firstName} ${c.lastName}`);
    setCustResults([]);
    setBilling(b => ({
      ...b,
      firstName: c.firstName || '',
      lastName:  c.lastName  || '',
      phone:     c.phone     || '',
      email:     c.email     || '',
      address:   c.address   || '',
      city:      c.city      || b.city,
      stPr:      c.stPr      || b.stPr,
      zipPostal: c.zipPostal || b.zipPostal,
      country:   c.country   || b.country,
    }));
  }

  function addItem() { setItems(it => [...it, emptyItem()]); }
  function removeItem(i) { setItems(it => it.filter((_,idx) => idx !== i)); }
  function updateItem(i, k, v) {
    setItems(it => it.map((item,idx) => idx===i ? { ...item, [k]: k==='qty'||k==='price'||k==='discount' ? parseFloat(v)||0 : v } : item));
  }

  function productSelected(i, productId) {
    const p = products.find(p => p.id === productId);
    if (p) updateItem(i, 'price', p.price);
    updateItem(i, 'productId', productId);
    if (p) updateItem(i, 'description', p.name);
  }

  async function handleSave(status) {
    setSaving(true); setError(''); setSuccess('');
    try {
      // Recipient only exists as a real concept for these types — carry-out
      // and pick-up customers collect their own order, so there's no one to
      // remember as "the recipient" for next time.
      const namedRecipient = `${recipient.firstName || ''}${recipient.lastName || ''}`.trim();
      const hasRecipient =
        ['local_delivery', 'wire_out', 'wire_in'].includes(type) ||
        // Carry-out / pick-up only have one when somebody ELSE is collecting;
        // mirroring the customer onto themselves isn't a recipient to remember.
        (!sameAsCustomer && !!namedRecipient);

      // Create or update the customer record BEFORE the order, so the order
      // can point at whichever customer id this resolves to — a brand new
      // walk-in gets a fresh record, a repeat caller (matched by phone) gets
      // their existing one updated instead of a duplicate.
      const customerId = await upsertCustomerFromOrder(shopId, {
        customerId: selectedCust?.id || null,
        billing,
        recipient: hasRecipient ? recipient : null,
      });

      const orderId = await createOrder(shopId, {
        type, status,
        // Pick-ups carry whatever was chosen; all other types are paid by the
        // time the order is completed.
        billing: { ...billing, paid: type === 'pick_up' ? !!billing.paid : true },
        recipient,
        // Normalised scheduling block — written for EVERY order type so the
        // workboard has one consistent field to read. The per-type blocks
        // below stay as-is for the printed ticket and existing screens.
        fulfillment: {
          neededDate:   delivery.date,
          neededTime:   delivery.time === 'None' ? '' : delivery.time,
          outOfTown,
          flight:       outOfTown ? { ...flight, date: flight.date || delivery.date } : null,
          designStatus: 'not_started',
          handedOff:    false,
          // Snapshotted so the board still explains itself if the shop later
          // changes its lead-time rules.
          leadDays:     preview.leadDays,
          startBy:      preview.startBy,
          finishBy:     preview.finishBy,
        },
        ...(type === 'local_delivery' && { delivery }),
        ...(type === 'pick_up' && { pickupDate: delivery.date, pickupTime: delivery.time }),
        ...(type === 'wire_out' && { wireOut, delivery }),
        ...(type === 'wire_in'  && { wireIn, delivery }),
        items: items.filter(i => i.description),
        ...((['wire_out','wire_in'].includes(type)) && { secondChoice: items2 }),
        subtotal, deliveryFee, serviceFee, tax, total,
        customerId:    customerId || selectedCust?.id || null,
        customerName:  `${billing.firstName} ${billing.lastName}`.trim(),
        receivedBy:    soldBy || profile?.displayName || '',
      });
      setSuccess(`Order saved! ID: ${orderId}`);
      // Refresh the customer list in the background so the new/updated
      // record (and any new recentRecipients) shows up in search right away,
      // without blocking the success message on it.
      getCustomers(shopId).then(setCustomers).catch(() => {});
      // Reset form
      setItems([emptyItem()]);
      setBilling(b => ({ ...b, firstName:'', lastName:'', cardNumber:'', ccv:'', expDate:'' }));
      setRecipient(r => ({ ...r, firstName:'', lastName:'', address:'', phone:'', cardMessage:'', occasion:'Other' }));
      setSelectedCust(null); setCustSearch('');
      setOutOfTown(false); setFlight({ airline:'', number:'', date:'', departTime:'' });
      setSameAsCustomer(false);
    } catch(e) {
      setError('Failed to save order: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  const setPaid = (v) => setBilling(b => ({ ...b, paid: v }));
  // Tidy the casing as it's typed. Names get each word capitalised, free text
  // gets sentence case — see src/lib/text.js, which only ever promotes a letter
  // to uppercase so the caret doesn't move and deliberate casing survives.
  const NAME_FIELDS = ['firstName', 'lastName', 'city', 'address'];
  const PROSE_FIELDS = ['cardMessage', 'instructions', 'notes'];
  const tidy = (k, v) =>
    typeof v !== 'string' ? v
    : NAME_FIELDS.includes(k)  ? titleCaseName(v)
    : PROSE_FIELDS.includes(k) ? sentenceCase(v)
    : v;

  const setB = (k) => (e) => setBilling(b => ({ ...b, [k]: e.target.type==='checkbox' ? e.target.checked : tidy(k, e.target.value) }));
  const setR = (k) => (e) => setRecipient(r => ({ ...r, [k]: tidy(k, e.target.value) }));
  const setD = (k) => (e) => setDelivery(d => ({ ...d, [k]: tidy(k, e.target.value) }));

  return (
    <div className="order-form">
      {/* Type selector */}
      <div style={{display:'flex',gap:8,marginBottom:10,flexWrap:'wrap'}}>
        {ORDER_TYPES.map(t => (
          <button
            key={t.id}
            className={`btn ${type === t.id ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setType(t.id)}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {success && <div style={{background:'var(--g100)',border:'1px solid var(--g300)',color:'var(--g700)',borderRadius:'var(--radius-sm)',padding:'10px 14px',marginBottom:14}}>{success}</div>}
      {error   && <div className="auth-error" style={{marginBottom:14}}>{error}</div>}

      {/* ── CARRY OUT & PICK UP ── */}
      {(type === 'carry_out' || type === 'pick_up') && (
        <div className="order-grid-main">
          <CustomerFormCard billing={billing} setB={setB} custSearch={custSearch} setCustSearch={setCustSearch} custResults={custResults} onSelect={selectCustomer}/>

          {/* Someone other than the buyer often collects a pick-up, so this
              block is needed here too — with a tick-box for the common case
              where it's the same person. */}
          <RecipientCard
            recipient={recipient} setR={setR} setRecipient={setRecipient}
            pastRecipients={selectedCust?.recentRecipients}
            sameAsCustomer={sameAsCustomer} setSameAsCustomer={setSameAsCustomer}
            compact
          />

          <div>
            {/* Carry-outs need a date too — without one they're invisible to
                the workboard, which is how orders got missed in the first place. */}
            <NeededCard
              title={type === 'pick_up' ? 'Pick Up' : 'Needed'}
              delivery={delivery} setD={setD}
              outOfTown={outOfTown} setOutOfTown={setOutOfTown}
              flight={flight} setFlight={setFlight}
              preview={preview} wf={wf}
            />
          </div>

          <div className="span2">
            <ProductsCard items={items} products={products} onAdd={addItem} onRemove={removeItem} onUpdate={updateItem} onProductSelect={productSelected}/>
          </div>
          <PaymentSummary subtotal={subtotal} deliveryFee={0} tax={tax} taxRate={taxRate} total={total} orderType={type} onSave={handleSave} onTakePayment={()=>setPayOpen(true)} saving={saving} onTaxRateChange={setCustomTaxRate}/>
        </div>
      )}

      {/* ── LOCAL DELIVERY ── */}
      {type === 'local_delivery' && (
        <>
          <div className="order-grid-main">
            <CustomerFormCard billing={billing} setB={setB} custSearch={custSearch} setCustSearch={setCustSearch} custResults={custResults} onSelect={selectCustomer}/>
            <RecipientCard recipient={recipient} setR={setR} setRecipient={setRecipient} pastRecipients={selectedCust?.recentRecipients}/>
            <DeliveryCard
              delivery={delivery} setD={setD} preview={preview} wf={wf}
              outOfTown={outOfTown} setOutOfTown={setOutOfTown} flight={flight} setFlight={setFlight}
            />
            <div className="span2">
              <ProductsCard items={items} products={products} onAdd={addItem} onRemove={removeItem} onUpdate={updateItem} onProductSelect={productSelected}/>
            </div>
            <PaymentSummary subtotal={subtotal} deliveryFee={deliveryFee} tax={tax} taxRate={taxRate} total={total} orderType={type} onSave={handleSave} onTakePayment={()=>setPayOpen(true)} saving={saving} onDeliveryChange={setCustomDelivery} onTaxRateChange={setCustomTaxRate}/>
          </div>
        </>
      )}

      {/* ── WIRE OUT ── */}
      {type === 'wire_out' && (
        <>
          <div className="order-grid-main">
            <CustomerFormCard billing={billing} setB={setB} custSearch={custSearch} setCustSearch={setCustSearch} custResults={custResults} onSelect={selectCustomer}/>
            <RecipientCard recipient={recipient} setR={setR} setRecipient={setRecipient} pastRecipients={selectedCust?.recentRecipients}/>
            <div className="card">
              <div className="section-label">Filling Florist</div>
              <div className="form-group"><label className="field-label">Delivery Date</label>
                <input className="input" type="date" value={delivery.date} onChange={setD('date')}/>
              </div>
              <div className="form-group"><label className="field-label">Instructions</label>
                <textarea className="input" rows={3} value={delivery.instructions} onChange={setD('instructions')}/>
              </div>
              <div className="form-group"><label className="field-label">Send Via</label>
                <select className="input" value={wireOut.sendVia} onChange={e=>setWireOut(w=>({...w,sendVia:e.target.value}))}>
                  <option>Teleflora-Dove</option><option>FTD</option><option>1-800-Flowers</option><option>BloomNet</option>
                </select>
              </div>
              <div className="form-group"><label className="field-label">Filling Florist</label>
                <input className="input" value={wireOut.fillingFlorist} onChange={e=>setWireOut(w=>({...w,fillingFlorist:e.target.value}))}/>
              </div>
              <div className="form-row">
                <div className="form-group flex1"><label className="field-label">Auto-forward</label>
                  <select className="input" value={wireOut.autoForward} onChange={e=>setWireOut(w=>({...w,autoForward:e.target.value}))}>
                    <option value="Y">Y – Automatically Forward</option><option value="N">N – Do Not Forward</option>
                  </select>
                </div>
              </div>
              <SchedulePreview preview={preview}/>
            </div>
            <div className="span2">
              <ProductsCard items={items} products={products} onAdd={addItem} onRemove={removeItem} onUpdate={updateItem} onProductSelect={productSelected} secondChoice={items2} setSecondChoice={setItems2}/>
            </div>
            <PaymentSummary subtotal={subtotal} serviceFee={serviceFee} tax={tax} taxRate={taxRate} total={total} orderType={type} onSave={handleSave} onTakePayment={()=>setPayOpen(true)} saving={saving} onTaxRateChange={setCustomTaxRate}/>
          </div>
        </>
      )}

      {/* ── WIRE IN ── */}
      {type === 'wire_in' && (
        <>
          <div className="order-grid-main">
            <div className="card">
              <div className="section-label">Sending Florist</div>
              <div className="form-row">
                <div className="form-group flex1"><label className="field-label">Received Via</label>
                  <select className="input" value={wireIn.receivedVia} onChange={e=>setWireIn(w=>({...w,receivedVia:e.target.value}))}>
                    <option>Teleflora-Phone</option><option>Teleflora-Wire</option><option>FTD</option><option>BloomNet</option><option>Phone</option>
                  </select>
                </div>
                <div className="form-group flex1"><label className="field-label">Delivery Type</label>
                  <select className="input" value={wireIn.deliveryType} onChange={e=>setWireIn(w=>({...w,deliveryType:e.target.value}))}>
                    <option>Local Delivery</option><option>Pick Up</option>
                  </select>
                </div>
              </div>
              <div className="form-group"><label className="field-label">Florist ID</label>
                <input className="input" value={wireIn.floristId} onChange={e=>setWireIn(w=>({...w,floristId:e.target.value}))}/>
              </div>
              <div className="form-group"><label className="field-label">Florist Information</label>
                <textarea className="input" rows={3} value={wireIn.floristInfo} onChange={e=>setWireIn(w=>({...w,floristInfo:e.target.value}))}/>
              </div>
              <div className="form-group"><label className="field-label">Contact Name</label>
                <input className="input" value={wireIn.contactName} onChange={e=>setWireIn(w=>({...w,contactName:e.target.value}))}/>
              </div>
              <div className="form-group"><label className="field-label">Reference Order #</label>
                <input className="input" value={wireIn.refOrderNumber} onChange={e=>setWireIn(w=>({...w,refOrderNumber:e.target.value}))}/>
              </div>
              <label className="checkbox-label"><input type="checkbox" checked={wireIn.dontReportTeleflora} onChange={e=>setWireIn(w=>({...w,dontReportTeleflora:e.target.checked}))}/> Do Not Report to Teleflora HQ</label>
            </div>
            <RecipientCard recipient={recipient} setR={setR} setRecipient={setRecipient} pastRecipients={selectedCust?.recentRecipients}/>
            <DeliveryCard
              delivery={delivery} setD={setD} preview={preview} wf={wf}
              outOfTown={outOfTown} setOutOfTown={setOutOfTown} flight={flight} setFlight={setFlight}
            />
            <div className="span2">
              <ProductsCard items={items} products={products} onAdd={addItem} onRemove={removeItem} onUpdate={updateItem} onProductSelect={productSelected} secondChoice={items2} setSecondChoice={setItems2}/>
            </div>
            <PaymentSummary subtotal={subtotal} deliveryFee={deliveryFee} tax={tax} taxRate={taxRate} total={total} orderType={type} onSave={handleSave} onTakePayment={()=>setPayOpen(true)} saving={saving} onDeliveryChange={setCustomDelivery} onTaxRateChange={setCustomTaxRate}/>
          </div>
        </>
      )}

      {payOpen && (
        <TakePaymentModal
          billing={billing} set={setB} setPaid={setPaid} total={total}
          orderType={type} saving={saving}
          employees={employees} soldBy={soldBy} setSoldBy={setSoldBy}
          onClose={()=>setPayOpen(false)}
          onConfirm={async ()=>{ await handleSave('completed'); setPayOpen(false); }}
        />
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function CustomerFormCard({ billing, setB, custSearch, setCustSearch, custResults, onSelect }) {
  return (
    <div className="card">
      <div className="section-label">Customer</div>
      <div style={{position:'relative',marginBottom:10}}>
        <input className="input" placeholder="Search by phone, name or ID"
          value={custSearch} onChange={e=>setCustSearch(e.target.value)}/>
        {custResults.length > 0 && (
          <div style={{position:'absolute',top:'100%',left:0,right:0,background:'white',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',boxShadow:'var(--shadow-md)',zIndex:50}}>
            {custResults.map(c => (
              <div key={c.id} style={{padding:'9px 12px',cursor:'pointer',fontSize:13}} onMouseDown={()=>onSelect(c)}>
                <strong>{c.firstName} {c.lastName}</strong>
                <span style={{color:'var(--text-muted)',marginLeft:8}}>{c.phone}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="form-row">
        <div className="form-group flex1"><label className="field-label">First Name</label><input className="input" value={billing.firstName} onChange={setB('firstName')}/></div>
        <div className="form-group flex1"><label className="field-label">Last / Business</label><input className="input" value={billing.lastName} onChange={setB('lastName')}/></div>
      </div>
      <div className="form-group"><label className="field-label">Address</label><textarea className="input" rows={2} value={billing.address} onChange={setB('address')}/></div>
      <div className="form-row">
        <div className="form-group flex1"><label className="field-label">City</label><input className="input" value={billing.city} onChange={setB('city')}/></div>
        <div className="form-group w-sm"><label className="field-label">St/Pr</label><input className="input" value={billing.stPr} onChange={setB('stPr')}/></div>
      </div>
      <div className="form-group"><label className="field-label">Country</label><select className="input" value={billing.country} onChange={setB('country')}><option>Canada</option><option>USA</option></select></div>
      <div className="form-row">
        <div className="form-group flex1"><label className="field-label">Phone</label><input className="input" type="tel" value={billing.phone} onChange={setB('phone')}/></div>
        <div className="form-group flex1"><label className="field-label">Email <span style={{fontWeight:400,color:'var(--text-muted)'}}>(optional — for invoices)</span></label><input className="input" type="email" value={billing.email} onChange={setB('email')}/></div>
      </div>
      <div className="form-group"><label className="field-label">Customer Notes</label><textarea className="input" rows={2}/></div>
    </div>
  );
}

/**
 * Paid / Not paid. Printed on the ticket so whoever hands the order over knows
 * at a glance whether to collect money — the single most consequential thing to
 * get wrong on a pick-up.
 */
function PaidToggle({ billing, setPaid, total }) {
  const paid = !!billing.paid;
  return (
    <div className="form-group">
      <label className="field-label">Payment status</label>
      <div style={{ display:'flex', gap:8 }}>
        <button type="button"
          className={`btn btn-sm ${paid ? 'btn-primary' : 'btn-secondary'}`}
          style={{ flex:1, justifyContent:'center' }}
          onClick={() => setPaid(true)}>
          ✓ Paid
        </button>
        <button type="button"
          className={`btn btn-sm ${!paid ? 'btn-danger' : 'btn-secondary'}`}
          style={{ flex:1, justifyContent:'center' }}
          onClick={() => setPaid(false)}>
          Not paid
        </button>
      </div>
      {!paid && total > 0 && (
        <small style={{ fontSize:11.5, color:'var(--red)', fontWeight:600 }}>
          Balance due {fmt(total)} — collect on pickup.
        </small>
      )}
    </div>
  );
}

function TakePaymentModal({ billing, set, setPaid, total, orderType, saving, onConfirm, onClose, employees, soldBy, setSoldBy }) {
  // Active staff only — an invited-but-not-joined account can't take an order.
  const names = (employees || [])
    .filter(e => e.status !== 'invited' && e.displayName)
    .map(e => e.displayName);
  const label = { carry_out:'Complete Sale', local_delivery:'Complete Sale',
                  pick_up:'Complete Sale', wire_out:'Complete Sale', wire_in:'Complete' }[orderType] || 'Complete Sale';
  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2>Take Payment</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div style={{
          display:'flex', justifyContent:'space-between', alignItems:'center',
          background:'var(--g50)', border:'1px solid var(--g300)',
          borderRadius:'var(--radius)', padding:'12px 16px', marginBottom:16,
        }}>
          <span style={{ fontWeight:700, color:'var(--g800)' }}>Amount due</span>
          <span style={{ fontSize:24, fontWeight:800, color:'var(--g700)' }}>{fmt(total)}</span>
        </div>

        {/* Only pick-ups can leave unpaid — the customer collects later. Every
            other type is settled before the flowers leave the shop. */}
        {orderType === 'pick_up' && <PaidToggle billing={billing} setPaid={setPaid} total={total}/>}

        {/* Whoever is signed in is preselected; the counter is often shared,
            so it can be changed without logging out and back in. */}
        <div className="form-group">
          <label className="field-label">Taken by</label>
          <select className="input" value={soldBy} onChange={e=>setSoldBy(e.target.value)}>
            {!names.includes(soldBy) && soldBy && <option>{soldBy}</option>}
            {names.map(n => <option key={n}>{n}</option>)}
            {names.length === 0 && !soldBy && <option value="">—</option>}
          </select>
        </div>

        <div className="form-group">
          <label className="field-label">Method</label>
          <select className="input" value={billing.method} onChange={set('method')} autoFocus>
            {['Credit Card','Cash','Debit','Check','Invoice'].map(m=><option key={m}>{m}</option>)}
          </select>
        </div>

        {billing.method === 'Credit Card' && <>
          <div className="form-row">
            <div className="form-group flex1"><label className="field-label">Card Number</label>
              <input className="input" value={billing.cardNumber} onChange={set('cardNumber')} autoComplete="off"/>
            </div>
            <div className="form-group w-sm"><label className="field-label">PO #</label>
              <input className="input" value={billing.poNumber} onChange={set('poNumber')}/>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group w-sm"><label className="field-label">Exp Date</label>
              <input className="input" placeholder="MM/YY" value={billing.expDate} onChange={set('expDate')}/>
            </div>
            <div className="form-group w-sm"><label className="field-label">CCV</label>
              <input className="input" value={billing.ccv} onChange={set('ccv')} autoComplete="off"/>
            </div>
            <div className="form-group flex1"><label className="field-label">Auth #</label>
              <input className="input" value={billing.auth} onChange={set('auth')}/>
            </div>
          </div>
          <p style={{fontSize:11.5,color:'var(--text-muted)',marginTop:-2,marginBottom:10}}>
            🔒 Only the last 4 digits and the auth number are saved. The full card
            number and CCV are never stored — run the card on the terminal.
          </p>
          <div style={{marginBottom:6}}>
            <label className="checkbox-label"><input type="checkbox" checked={billing.keepOnFile} onChange={set('keepOnFile')}/> Keep Card On File</label>
            <label className="checkbox-label"><input type="checkbox" checked={billing.internetTx} onChange={set('internetTx')}/> Internet Transaction</label>
            <label className="checkbox-label"><input type="checkbox" checked={billing.dontProcess} onChange={set('dontProcess')}/> Do Not Process Card</label>
          </div>
        </>}

        <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:16}}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={saving} onClick={onConfirm}>
            {saving ? 'Saving…' : label}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * pastRecipients comes from the selected customer's record — up to 5 people
 * they've sent flowers to before, most recent first. Picking one fills the
 * whole block instead of retyping an address every repeat order.
 */
function RecipientCard({ recipient, setR, setRecipient, pastRecipients, sameAsCustomer, setSameAsCustomer, compact }) {
  const [recipSearch, setRecipSearch] = useState('');
  const [showList,    setShowList]    = useState(false);
  const mirrored = !!sameAsCustomer;

  const matches = (pastRecipients || [])
    .filter(r => {
      if (!recipSearch.trim()) return true;
      const t = recipSearch.toLowerCase();
      return `${r.firstName} ${r.lastName}`.toLowerCase().includes(t);
    })
    .slice(0, 3);

  function pickRecipient(r) {
    setRecipient(cur => ({
      ...cur,
      firstName: r.firstName || '', lastName: r.lastName || '',
      address:   r.address   || '', city:     r.city     || cur.city,
      stPr:      r.stPr      || cur.stPr, zipPostal: r.zipPostal || '',
      country:   r.country   || cur.country, phone: r.phone || '',
      occasion:  r.occasion  || cur.occasion,
    }));
    setRecipSearch(''); setShowList(false);
  }

  return (
    <div className="card">
      <div className="section-label">Recipient</div>

      {/* Most pick-ups and carry-outs are collected by the person who ordered.
          Ticking this mirrors the customer's details across and keeps them in
          step as they're typed, so the block doesn't have to be filled twice. */}
      {setSameAsCustomer && (
        <label className="checkbox-label" style={{ marginBottom: 8 }}>
          <input
            type="checkbox"
            checked={mirrored}
            onChange={e => setSameAsCustomer(e.target.checked)}
          />
          {' '}Same as customer
        </label>
      )}

      {!mirrored && pastRecipients && pastRecipients.length > 0 && (
        <div style={{ position:'relative', marginBottom:10 }}>
          <input
            className="input"
            placeholder={`Search this customer's past recipients (${pastRecipients.length})`}
            value={recipSearch}
            onChange={e => { setRecipSearch(e.target.value); setShowList(true); }}
            onFocus={() => setShowList(true)}
            onBlur={() => setTimeout(() => setShowList(false), 120)}
          />
          {showList && matches.length > 0 && (
            <div style={{
              position:'absolute', top:'100%', left:0, right:0, zIndex:50,
              background:'white', border:'1px solid var(--border)',
              borderRadius:'var(--radius-sm)', boxShadow:'var(--shadow-md)',
            }}>
              {matches.map((r, i) => (
                <div key={i}
                  style={{ padding:'9px 12px', cursor:'pointer', fontSize:13 }}
                  onMouseDown={() => pickRecipient(r)}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--parch)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                  <strong>{r.firstName} {r.lastName}</strong>
                  {r.address && <span style={{ color:'var(--text-muted)', marginLeft:8 }}>{r.address}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="form-row">
        <div className="form-group flex1"><label className="field-label">First Name</label><input className="input" value={recipient.firstName} disabled={mirrored} onChange={setR('firstName')}/></div>
        <div className="form-group flex1"><label className="field-label">Last Name</label><input className="input" value={recipient.lastName} disabled={mirrored} onChange={setR('lastName')}/></div>
      </div>
      {/* A counter collection has nowhere to deliver to, so the address block
          is dropped for carry-out and pick-up — only who's collecting matters. */}
      {!compact && <>
        <div className="form-group"><label className="field-label">Address</label><input className="input" value={recipient.address} disabled={mirrored} onChange={setR('address')}/></div>
        <div className="form-row">
          <div className="form-group flex1"><label className="field-label">City</label><input className="input" value={recipient.city} disabled={mirrored} onChange={setR('city')}/></div>
          <div className="form-group w-sm"><label className="field-label">St/Pr</label><input className="input" value={recipient.stPr} disabled={mirrored} onChange={setR('stPr')}/></div>
        </div>
        <div className="form-group"><label className="field-label">Country</label><select className="input" value={recipient.country} disabled={mirrored} onChange={setR('country')}><option>Canada</option><option>USA</option></select></div>
      </>}
      {/* Without the address block there's room to pair these up, which keeps
          the counter-collection form on one screen. */}
      {compact ? (
        <div className="form-row">
          <div className="form-group flex1"><label className="field-label">Phone</label>
            <input className="input" value={recipient.phone} disabled={mirrored} onChange={setR('phone')}/>
          </div>
          <div className="form-group flex1"><label className="field-label">Occasion</label>
            <select className="input" value={recipient.occasion} onChange={setR('occasion')}>
              {OCCASIONS.map(o=><option key={o}>{o}</option>)}
            </select>
          </div>
        </div>
      ) : (
        <div className="form-group"><label className="field-label">Phone</label><input className="input" value={recipient.phone} disabled={mirrored} onChange={setR('phone')}/></div>
      )}

      <div className="form-group"><label className="field-label">Card Message</label><textarea className="input" rows={3} value={recipient.cardMessage} onChange={setR('cardMessage')} placeholder="Write your message here..."/></div>

      {!compact && (
        <div className="form-group"><label className="field-label">Occasion</label>
          <select className="input" value={recipient.occasion} onChange={setR('occasion')}>
            {OCCASIONS.map(o=><option key={o}>{o}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

/**
 * Shows the person taking the order exactly which day it has to be made,
 * before they hang up the phone. Same rules engine the workboard runs on.
 */
function SchedulePreview({ preview }) {
  if (!preview.neededDate) {
    return (
      <div className="lead-preview tone-red">
        ⚠️ <strong>No date set.</strong> This order won't appear on the workboard until it has one.
      </div>
    );
  }

  const tone = preview.urgency === 'late' || preview.urgency === 'today' ? 'red'
             : preview.reasons.length > 0                               ? 'amber'
             : '';
  const late = preview.finishBy && preview.finishBy < todayStr();
  const finishPhrase = relativePhrase(preview.finishBy);

  return (
    <div className={`lead-preview tone-${tone}`}>
      <div>
        🌸 Make this by <strong>{fmtDay(preview.finishBy)}</strong>
        {finishPhrase && <> ({finishPhrase})</>}
        {late && <> — <strong>that's already passed, this is a rush</strong></>}
      </div>
      {preview.startBy !== preview.finishBy && (
        <div>⏳ Start it by <strong>{fmtDay(preview.startBy)}</strong> — {preview.reasons.map(r => r.label).join(', ')}</div>
      )}
      {preview.dropOffBy && (
        <div>✈️ At the airport by <strong>{fmtTime(preview.dropOffBy)}</strong> on {fmtDay(preview.flightDate)}</div>
      )}
    </div>
  );
}

/** Needed-by date + optional flight details. Used by carry-out and pick-up. */
function NeededCard({ title, delivery, setD, outOfTown, setOutOfTown, flight, setFlight, preview, wf }) {
  return (
    <div className="card">
      <div className="section-label">{title}</div>
      <div className="form-row">
        <div className="form-group flex1"><label className="field-label">Needed Date *</label>
          <input className="input" type="date" value={delivery.date} onChange={setD('date')}/>
        </div>
        <div className="form-group flex1"><label className="field-label">Time</label>
          <input className="input" type="time" value={delivery.time === 'None' ? '' : delivery.time} onChange={setD('time')}/>
        </div>
      </div>
      <OutOfTownFields outOfTown={outOfTown} setOutOfTown={setOutOfTown} flight={flight} setFlight={setFlight} neededDate={delivery.date}/>
      <SchedulePreview preview={preview}/>
    </div>
  );
}

function OutOfTownFields({ outOfTown, setOutOfTown, flight, setFlight, neededDate }) {
  const setF = (k) => (e) => setFlight(f => ({ ...f, [k]: e.target.value }));
  // An unset flight date means "same day it's needed" — show that rather than
  // an empty box, so the field matches what actually gets saved.
  const flightDate = flight.date || neededDate || '';
  return (
    <>
      <label className="checkbox-label">
        <input type="checkbox" checked={outOfTown} onChange={e => setOutOfTown(e.target.checked)}/>
        {' '}Going out of town (flying) — needs extra lead time
      </label>
      {outOfTown && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--border)' }}>
          <div className="form-row">
            <div className="form-group flex1"><label className="field-label">Airline</label>
              <input className="input" value={flight.airline} onChange={setF('airline')} placeholder="Canadian North"/>
            </div>
            <div className="form-group w-sm"><label className="field-label">Flight #</label>
              <input className="input" value={flight.number} onChange={setF('number')} placeholder="5T123"/>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group flex1"><label className="field-label">Flight Date</label>
              <input className="input" type="date" value={flightDate} onChange={setF('date')}/>
            </div>
            <div className="form-group flex1"><label className="field-label">Departs</label>
              <input className="input" type="time" value={flight.departTime} onChange={setF('departTime')}/>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function DeliveryCard({ delivery, setD, outOfTown, setOutOfTown, flight, setFlight, preview, wf, children }) {
  return (
    <div className="card">
      <div className="section-label">Delivery</div>
      <div className="form-row">
        <div className="form-group flex1"><label className="field-label">Delivery Date</label><input className="input" type="date" value={delivery.date} onChange={setD('date')}/></div>
        <div className="form-group flex1"><label className="field-label">Status</label>
          <select className="input" value={delivery.status} onChange={setD('status')}>
            <option>Ready</option><option>Pending</option><option>Out for Delivery</option><option>Delivered</option>
          </select>
        </div>
      </div>
      <div className="form-group"><label className="field-label">Delivery Time</label>
        <select className="input" value={delivery.time} onChange={setD('time')}><option>None</option><option>AM</option><option>Noon</option><option>PM</option><option>Evening</option></select>
      </div>
      <div className="form-row">
        <div className="form-group flex1"><label className="field-label">Zone</label><input className="input" value={delivery.zone} onChange={setD('zone')}/></div>
        <div className="form-group flex1"><label className="field-label">Driver</label><input className="input" value={delivery.driver} onChange={setD('driver')}/></div>
      </div>
      <div className="form-row">
        <div className="form-group flex1"><label className="field-label">Design Status</label>
          <select className="input" value={delivery.designStatus} onChange={setD('designStatus')}><option>Pending</option><option>In Progress</option><option>Complete</option></select>
        </div>
        <div className="form-group flex1"><label className="field-label">Designer</label>
          <input className="input" value={delivery.designer} onChange={setD('designer')}/>
        </div>
      </div>
      <div className="form-group"><label className="field-label">Instructions</label><textarea className="input" rows={2} value={delivery.instructions} onChange={setD('instructions')}/></div>
      {setOutOfTown && (
        <OutOfTownFields outOfTown={outOfTown} setOutOfTown={setOutOfTown} flight={flight} setFlight={setFlight} neededDate={delivery.date}/>
      )}
      {preview && <SchedulePreview preview={preview}/>}
      {children}
    </div>
  );
}

function ProductsCard({ items, products, onAdd, onRemove, onUpdate, onProductSelect, secondChoice, setSecondChoice }) {
  return (
    <div className="card">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div className="section-label" style={{marginBottom:0}}>Products</div>
        {/* The title is positioned onto the panel border, so it no longer takes
            part in this flex row — the button pushes itself right instead. */}
        <button className="btn btn-ghost btn-sm" style={{marginLeft:'auto'}} onClick={onAdd}>+ Add Item</button>
      </div>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr><th></th><th>Product</th><th>Description</th><th>Qty</th><th>Price</th><th>Disc%</th><th>Ext. Price</th></tr>
          </thead>
          <tbody>
            {items.map((item, i) => {
              const ext = item.qty * item.price * (1 - item.discount/100);
              return (
                <tr key={i}>
                  <td><button className="btn btn-danger btn-sm btn-icon-only" onClick={()=>onRemove(i)}>✕</button></td>
                  <td>
                    <select className="input sm" value={item.productId} onChange={e=>onProductSelect(i,e.target.value)}>
                      <option value="">—</option>
                      {products.map(p=><option key={p.id} value={p.id}>{p.code || p.name}</option>)}
                    </select>
                  </td>
                  {/* A textarea, not an input, so Enter starts a new line —
                      descriptions often run to two or three. It grows with the
                      text rather than scrolling. */}
                  <td>
                    <textarea
                      className="input tk-desc"
                      rows={1}
                      value={item.description}
                      onChange={e=>onUpdate(i,'description',e.target.value)}
                      ref={el => autoGrow(el)}
                      onInput={e => autoGrow(e.target)}
                    />
                  </td>
                  {/* Numeric cells show blank rather than 0, so typing 65 gives
                      65 and not 065, and a click selects what's there. */}
                  <td><input className="input sm" type="number" min="1" value={item.qty || ''}
                        onFocus={e=>e.target.select()} placeholder="0"
                        onChange={e=>onUpdate(i,'qty',e.target.value)}/></td>
                  <td><input className="input sm" type="number" min="0" step="0.01" value={item.price || ''}
                        onFocus={e=>e.target.select()} placeholder="0.00"
                        onChange={e=>onUpdate(i,'price',e.target.value)}/></td>
                  <td><input className="input sm" type="number" min="0" max="100" value={item.discount || ''}
                        onFocus={e=>e.target.select()} placeholder="0"
                        onChange={e=>onUpdate(i,'discount',e.target.value)}/></td>
                  <td style={{fontWeight:600,color:'var(--g700)'}}>{fmt(ext)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {secondChoice && (
        <div style={{marginTop:14,paddingTop:12,borderTop:'1px dashed var(--border)'}}>
          <div style={{fontSize:11,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.7px',marginBottom:8}}>Second Choice Product</div>
          <div className="form-row">
            <input className="input sm" placeholder="Code" value={secondChoice[0]?.productId||''} onChange={e=>setSecondChoice([{...secondChoice[0],productId:e.target.value}])}/>
            <input className="input" placeholder="Description (similar, etc.)" value={secondChoice[0]?.description||''} onChange={e=>setSecondChoice([{...secondChoice[0],description:e.target.value}])}/>
            <small style={{color:'var(--text-muted)',fontSize:11.5,alignSelf:'center',whiteSpace:'nowrap'}}>Please enter total including delivery.</small>
          </div>
        </div>
      )}
    </div>
  );
}

// displayValue is what the user sees and types; onChange receives that same number.
// Caller is responsible for any unit conversion (e.g. % → decimal).
function EditableValue({ displayValue, onChange, prefix='$', suffix='', decimals=2 }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState('');

  function start() { setDraft(displayValue.toFixed(decimals)); setEditing(true); }
  function commit() {
    const n = parseFloat(draft);
    if (!isNaN(n) && n >= 0) onChange(n);
    setEditing(false);
  }

  if (editing) {
    return (
      <input
        autoFocus
        type="number"
        min="0"
        step={decimals === 1 ? '0.1' : '0.01'}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        style={{width:70, padding:'2px 5px', fontSize:13.5, border:'1px solid var(--g500)', borderRadius:4, textAlign:'right'}}
      />
    );
  }
  return (
    <span
      onClick={start}
      title="Click to edit"
      style={{cursor:'pointer', borderBottom:'1px dashed var(--g500)', color:'var(--g700)'}}
    >
      {prefix}{displayValue.toFixed(decimals)}{suffix}
    </span>
  );
}

function PaymentSummary({ subtotal, deliveryFee=0, serviceFee=0, tax, taxRate, total, orderType, onSave, onTakePayment, saving, inline, onDeliveryChange, onTaxRateChange }) {
  const completeLabel = { carry_out:'Complete Sale', local_delivery:'Take Payment', pick_up:'Complete Sale', wire_out:'Take Payment', wire_in:'Complete' };
  return (
    <div className={inline ? '' : 'card'} style={inline ? {marginTop:14,paddingTop:12,borderTop:'1px solid var(--parch-mid)'} : {}}>
      {!inline && <div className="section-label">Payment Summary</div>}
      <div className="summary-row"><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
      {deliveryFee > 0 && (
        <div className="summary-row">
          <span>Delivery Fee</span>
          {onDeliveryChange
            ? <EditableValue displayValue={deliveryFee} onChange={onDeliveryChange}/>
            : <span>{fmt(deliveryFee)}</span>}
        </div>
      )}
      {serviceFee > 0 && <div className="summary-row"><span>Service Fee</span><span>{fmt(serviceFee)}</span></div>}
      <div className="summary-row">
        <span>
          Tax{onTaxRateChange && taxRate != null && (
            <> (<EditableValue displayValue={taxRate * 100} onChange={v => onTaxRateChange(v / 100)} suffix="%" prefix="" decimals={1}/>)</>
          )}
        </span>
        <span>{fmt(tax)}</span>
      </div>
      <div className="summary-row total"><span>Total</span><span>{fmt(total)}</span></div>
      <div style={{display:'flex',gap:8,marginTop:14,flexWrap:'wrap'}}>
        {/* Opens the Take Payment dialog. Card capture lives there, not on the
            form, so the order screen fits without scrolling. */}
        <button className="btn btn-primary" disabled={saving} onClick={onTakePayment}>
          {completeLabel[orderType] || 'Complete'}
        </button>
        <button className="btn btn-secondary" disabled={saving} onClick={()=>onSave('draft')}>Save to Draft</button>
        <button className="btn btn-danger" disabled={saving} onClick={()=>onSave('void')}>Void</button>
      </div>
    </div>
  );
}
